/**
 * Role definitions and enums
 * Consolidated role system based on database structure
 */

export enum UserRole {
  ADMIN = 'ADMIN',
  PURCHASEMANAGER = 'PURCHASEMANAGER',
  SALESMANAGER = 'SALESMANAGER', 
  INVENTORYMANAGER = 'INVENTORYMANAGER',
  CATALOGMANAGER = 'CATALOGMANAGER',
  OPERATIONSUSER = 'OPERATIONSUSER',
  VIEWER = 'VIEWER'
}

export interface RoleDefinition {
  id: UserRole;
  displayName: string;
  description: string;
  isActive: boolean;
  hierarchy: number; // Lower number = higher privilege
}

export const ROLE_DEFINITIONS: RoleDefinition[] = [
  {
    id: UserRole.ADMIN,
    displayName: 'Administrator',
    description: 'Full system access and administration',
    isActive: true,
    hierarchy: 1
  },
  {
    id: UserRole.INVENTORYMANAGER,
    displayName: 'Inventory Manager',
    description: 'Manages inventory operations and reporting',
    isActive: true,
    hierarchy: 2
  },
  {
    id: UserRole.SALESMANAGER,
    displayName: 'Sales Manager',
    description: 'Manages sales operations and customers',
    isActive: true,
    hierarchy: 2
  },
  {
    id: UserRole.PURCHASEMANAGER,
    displayName: 'Purchase Manager',
    description: 'Manages purchasing and supplier operations',
    isActive: true,
    hierarchy: 2
  },
  {
    id: UserRole.CATALOGMANAGER,
    displayName: 'Catalog Manager',
    description: 'Manages product catalog and master data',
    isActive: true,
    hierarchy: 3
  },
  {
    id: UserRole.OPERATIONSUSER,
    displayName: 'Operations User',
    description: 'Day-to-day operational tasks',
    isActive: true,
    hierarchy: 4
  },
  {
    id: UserRole.VIEWER,
    displayName: 'Viewer',
    description: 'Read-only access to reports and dashboards',
    isActive: true,
    hierarchy: 5
  }
];

// Helper functions
export function getRoleDefinition(role: UserRole): RoleDefinition | undefined {
  return ROLE_DEFINITIONS.find(r => r.id === role);
}

// Role ID to UserRole mapping based on database
const ROLE_ID_MAPPING: Record<number, UserRole> = {
  6: UserRole.VIEWER, // Customer maps to Viewer for now
  7: UserRole.ADMIN,
  8: UserRole.PURCHASEMANAGER,
  9: UserRole.SALESMANAGER,
  10: UserRole.INVENTORYMANAGER,
  11: UserRole.CATALOGMANAGER,
  12: UserRole.OPERATIONSUSER,
  13: UserRole.VIEWER
};

export function getUserRoleFromString(roleString: string | null | undefined): UserRole | null {
  if (!roleString) return null;
  
  // Try parsing as number first (role ID)
  const roleId = parseInt(roleString);
  if (!isNaN(roleId) && ROLE_ID_MAPPING[roleId]) {
    return ROLE_ID_MAPPING[roleId];
  }
  
  const upperRole = roleString.toUpperCase();
  if (Object.values(UserRole).includes(upperRole as UserRole)) {
    return upperRole as UserRole;
  }
  
  // Handle common variations and display names
  const roleMappings: Record<string, UserRole> = {
    'ADMINISTRATOR': UserRole.ADMIN,
    'CATALOG_MANAGER': UserRole.CATALOGMANAGER,
    'INVENTORY_MANAGER': UserRole.INVENTORYMANAGER,
    'SALES_MANAGER': UserRole.SALESMANAGER,
    'PURCHASE_MANAGER': UserRole.PURCHASEMANAGER,
    'OPERATIONS_USER': UserRole.OPERATIONSUSER,
    'READONLY': UserRole.VIEWER,
    'CUSTOMER': UserRole.VIEWER, // Map customer to viewer
    // Display name mappings (with spaces)
    'INVENTORY MANAGER': UserRole.INVENTORYMANAGER,
    'SALES MANAGER': UserRole.SALESMANAGER,
    'PURCHASE MANAGER': UserRole.PURCHASEMANAGER,
    'CATALOG MANAGER': UserRole.CATALOGMANAGER,
    'OPERATIONS USER': UserRole.OPERATIONSUSER
  };
  
  return roleMappings[upperRole] || null;
}

export function getUserRoleFromId(roleId: number): UserRole | null {
  return ROLE_ID_MAPPING[roleId] || null;
}

export function isValidRole(role: string): boolean {
  return getUserRoleFromString(role) !== null;
}

export function getRoleHierarchy(role: UserRole): number {
  const definition = getRoleDefinition(role);
  return definition?.hierarchy || 999;
}

export function hasHigherOrEqualPrivilege(userRole: UserRole, requiredRole: UserRole): boolean {
  return getRoleHierarchy(userRole) <= getRoleHierarchy(requiredRole);
}
